import { useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
    AlertTriangle, Loader2, Trash2, ShieldCheck, PlayCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';

/**
 * Danger-zone card: wipe all transactional/test business data via
 * `POST /api/admin/cleanup`.
 *
 * The card mirrors the CLI script's safety layers:
 *   1. "Preview (dry-run)" is the default primary action — no writes.
 *   2. "Execute cleanup" is disabled until the backup checkbox is ticked.
 *   3. On execute click, a modal demands the user re-type the exact
 *      confirmation phrase (matches the CLI's DB-name prompt).
 *   4. The full audit report (before/after counts, deleted totals,
 *      preservation drift) is displayed in a modal after either run.
 */
const CONFIRM_PHRASE = 'RESET TEST DATA';

const ResetTestDataCard = () => {
    const [backupConfirmed, setBackupConfirmed] = useState(false);
    const [wipeStockLedger, setWipeStockLedger] = useState(false);
    const [running, setRunning] = useState(false);
    const [report, setReport] = useState(null);         // last CleanupReport JSON
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [typedConfirm, setTypedConfirm] = useState('');

    const callCleanup = async ({ execute }) => {
        setRunning(true);
        try {
            const { data } = await api.post('/admin/cleanup', {
                execute,
                backup_confirmed: execute ? backupConfirmed : false,
                wipe_stock_ledger: wipeStockLedger,
            });
            setReport(data);
            toast.success(
                execute
                    ? `Cleanup complete — ${data.total_deleted} docs deleted in ${data.elapsed_seconds.toFixed(2)}s`
                    : `Dry-run: would delete ${data.total_deleted} docs`,
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Cleanup failed');
        } finally {
            setRunning(false);
        }
    };

    const startExecute = () => {
        if (!backupConfirmed) {
            toast.error('Confirm the Atlas backup before executing.');
            return;
        }
        setTypedConfirm('');
        setConfirmOpen(true);
    };

    const finishExecute = async () => {
        if (typedConfirm.trim() !== CONFIRM_PHRASE) {
            toast.error(`Type "${CONFIRM_PHRASE}" exactly to proceed.`);
            return;
        }
        setConfirmOpen(false);
        await callCleanup({ execute: true });
    };

    return (
        <>
            <Card
                className="border-2 border-[#E76F51]/40 shadow-sm"
                data-testid="reset-test-data-card"
            >
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2 text-[#E76F51]">
                        <AlertTriangle size={18} />
                        Danger Zone — Reset Test Data
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                    <div className="rounded-md bg-[#FBF3EF] border border-[#E76F51]/20 p-3 text-[13px] leading-relaxed">
                        <p className="font-medium text-[#1A1A1A] mb-1">
                            Permanently deletes all transactional data:
                        </p>
                        <p className="text-gray-600">
                            CRM Customers, B2B Portal Customers &amp; Carts, Invoices,
                            Quotations, Drafts, Cloud Orders, and the report audit log.
                            Invoice / quotation / customer / cloud-order counters are
                            reset — the next invoice will be numbered <span className="font-mono">…-00001</span>.
                        </p>
                        <p className="mt-2 font-medium text-[#1A1A1A]">Preserved (never touched):</p>
                        <p className="text-gray-600">
                            Admin &amp; staff accounts, products, brands, categories,
                            suppliers, inventory batches, and uploaded media.
                        </p>
                    </div>

                    {/* Options */}
                    <div className="space-y-2">
                        <label
                            htmlFor="backup-confirmed"
                            className="flex items-start gap-2 cursor-pointer"
                        >
                            <Checkbox
                                id="backup-confirmed"
                                checked={backupConfirmed}
                                onCheckedChange={setBackupConfirmed}
                                data-testid="reset-backup-confirmed"
                            />
                            <span className="text-[13px] leading-snug">
                                I have taken a fresh Atlas backup (mongodump or Atlas snapshot)
                                and can restore it if needed.
                            </span>
                        </label>
                        <label
                            htmlFor="wipe-stock-ledger"
                            className="flex items-start gap-2 cursor-pointer"
                        >
                            <Checkbox
                                id="wipe-stock-ledger"
                                checked={wipeStockLedger}
                                onCheckedChange={setWipeStockLedger}
                                data-testid="reset-wipe-stock-ledger"
                            />
                            <span className="text-[13px] leading-snug text-gray-600">
                                Also delete the <span className="font-mono">stock_movements</span> audit
                                ledger (rare — full inventory-history reset).
                            </span>
                        </label>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-1">
                        <Button
                            variant="outline"
                            onClick={() => callCleanup({ execute: false })}
                            disabled={running}
                            data-testid="reset-preview-btn"
                            className="gap-2"
                        >
                            {running
                                ? <Loader2 size={14} className="animate-spin" />
                                : <PlayCircle size={14} />}
                            Preview (dry-run)
                        </Button>
                        <Button
                            onClick={startExecute}
                            disabled={running || !backupConfirmed}
                            data-testid="reset-execute-btn"
                            className="gap-2 bg-[#E76F51] hover:bg-[#c85942] text-white"
                        >
                            <Trash2 size={14} />
                            Execute cleanup
                        </Button>
                    </div>

                    {/* Last report inline summary */}
                    {report && (
                        <div
                            className="mt-4 rounded-md bg-white border p-3 text-[12px] font-mono max-h-72 overflow-y-auto"
                            data-testid="reset-report-inline"
                        >
                            <div className="flex items-center gap-2 mb-2 text-[13px] font-sans">
                                <ShieldCheck size={14} className="text-[#2A9D8F]" />
                                <span className="font-semibold">
                                    {report.mode === 'execute' ? 'Cleanup complete' : 'Dry-run report'}
                                </span>
                                <span className="text-gray-500">
                                    · {new Date(report.finished_at).toLocaleString()}
                                </span>
                                <span className="text-gray-500">
                                    · invoked by <span className="font-mono">{report.invoked_by}</span>
                                </span>
                            </div>
                            <pre className="whitespace-pre-wrap text-[11px]">
                                {(report.log || []).join('\n')}
                            </pre>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Final confirmation modal */}
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent data-testid="reset-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#E76F51] flex items-center gap-2">
                            <AlertTriangle size={18} />
                            Final confirmation
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <p>
                            This will permanently delete all transactional business data.
                            Master data (users, products, brands, categories, suppliers) is preserved.
                        </p>
                        <p>
                            To proceed, type the phrase{' '}
                            <span className="font-mono font-semibold text-[#E76F51]">
                                {CONFIRM_PHRASE}
                            </span>{' '}
                            below.
                        </p>
                        <Input
                            autoFocus
                            value={typedConfirm}
                            onChange={(e) => setTypedConfirm(e.target.value)}
                            placeholder={CONFIRM_PHRASE}
                            data-testid="reset-confirm-input"
                            className="font-mono"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={finishExecute}
                            disabled={typedConfirm.trim() !== CONFIRM_PHRASE}
                            data-testid="reset-confirm-btn"
                            className="bg-[#E76F51] hover:bg-[#c85942] text-white"
                        >
                            Delete transactional data
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ResetTestDataCard;
